import { useEffect, useState } from "react";
import { cn } from "../../core/helpers/cn";

export type ToastType = "success" | "error";

interface ToastProps {
  type: ToastType;
  title: string;
  description: string;
  onClose: () => void;
  duration?: number;
}

const Toast = ({
  type,
  title,
  description,
  onClose,
  duration = 3000,
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className="fixed backdrop-brightness-[0.2] inset-0 z-50">
      <div
        className={cn(
          "fixed top-16 left-1/2 transform -translate-x-1/2 transition-all duration-300",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-3 px-4 py-3 rounded-[36px] bg-white z-50 shadow-[0_4px_48px_0_rgba(0,0,0,0.08)] min-w-[300px] max-w-[90vw]"
          )}
        >
          <div
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            )}
          >
            {type === "success" ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM17.5303 9.53033C17.8232 9.23744 17.8232 8.76256 17.5303 8.46967C17.2374 8.17678 16.7626 8.17678 16.4697 8.46967L10 14.9393L7.53033 12.4697C7.23744 12.1768 6.76256 12.1768 6.46967 12.4697C6.17678 12.7626 6.17678 13.2374 6.46967 13.5303L9.46967 16.5303C9.76256 16.8232 10.2374 16.8232 10.5303 16.5303L17.5303 9.53033Z"
                  fill="#34C759"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM7.2272 16.7731C6.93431 16.4802 6.93431 16.0054 7.2272 15.7125L10.9395 12.0002L7.2272 8.28786C6.93431 7.99496 6.93431 7.52009 7.2272 7.2272C7.52009 6.9343 7.99497 6.9343 8.28786 7.2272L12.0002 10.9395L15.7125 7.2272C16.0054 6.9343 16.4802 6.9343 16.7731 7.2272C17.066 7.52009 17.066 7.99497 16.7731 8.28786L13.0608 12.0002L16.7731 15.7125C17.066 16.0054 17.066 16.4802 16.7731 16.7731C16.4802 17.066 16.0054 17.066 15.7125 16.7731L12.0002 13.0608L8.28786 16.7731C7.99497 17.066 7.52009 17.066 7.2272 16.7731Z"
                  fill="#FF383C"
                />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-base">{title}</div>
            <div className="text-sm text-[#43424299]">{description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
